import React, { useState } from "react";
import { ChevronRight, Landmark, Plus } from "lucide-react";

import EmptyState from "../../components/data-table/EmptyState";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { useBankName } from "../../hooks/use-bank-name";
import { useBusiness } from "../../hooks/use-business";
import { cn } from "../../utils/cn";
import AddBanks from "../banks/AddBanks";
import BankDetail from "../banks/BankDetail";
import SettingsSection from "./components/SettingsSection";

type Bank = {
  _id: string;
  accountName?: string;
  accountNumber?: string;
  bankCode?: string;
  isDefault?: boolean;
};

const Banks = () => {
  const { banks, businessId, isLoading } = useBusiness();
  const [openAddBank, setOpenAddBank] = useState(false);
  const [bankId, setBankId] = useState<string | null>(null);

  // Bank codes come back on the account; the display name is looked up here.
  const { bankName } = useBankName();

  // Default account first — it's the one payouts settle into.
  const sorted: Bank[] = [...((banks as Bank[]) ?? [])].sort(
    (a, b) => Number(!!b.isDefault) - Number(!!a.isDefault)
  );

  return (
    <div className="space-y-6">
      <SettingsSection
        title="Payout accounts"
        description="Settlements are paid into your default account."
        action={
          <Button
            onClick={() => setOpenAddBank(true)}
            disabled={!businessId}
            className="h-10 gap-2 rounded-xl px-5 text-[14px] font-semibold"
          >
            <Plus className="size-4" />
            Add bank
          </Button>
        }
      >
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-[84px] w-full rounded-xl" />
            <Skeleton className="h-[84px] w-full rounded-xl" />
          </div>
        ) : sorted.length === 0 ? (
          <EmptyState
            title="No bank account added"
            description="Add a bank account so your payouts have somewhere to settle."
            action={
              <Button
                onClick={() => setOpenAddBank(true)}
                disabled={!businessId}
                className="mt-2 gap-2"
              >
                <Plus className="size-4" />
                Add your first bank
              </Button>
            }
          />
        ) : (
          <ul className="space-y-3">
            {sorted.map((bank) => (
              <li key={bank._id}>
                <button
                  type="button"
                  onClick={() => setBankId(bank._id)}
                  className={cn(
                    "flex w-full items-center gap-4 rounded-xl border p-4 text-left transition-colors hover:bg-muted",
                    bank.isDefault && "border-primary/40 bg-accent/50"
                  )}
                >
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-accent text-primary">
                    <Landmark className="size-5" />
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-[14px] font-semibold text-foreground">
                        {bank.accountName || "Unnamed account"}
                      </span>
                      {bank.isDefault && (
                        <Badge className="shrink-0">Default</Badge>
                      )}
                    </span>
                    <span className="mt-0.5 block truncate text-[13px] font-medium text-muted-foreground">
                      {bank.accountNumber}
                      {bankName(bank.bankCode)
                        ? ` • ${bankName(bank.bankCode)}`
                        : ""}
                    </span>
                  </span>

                  <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </SettingsSection>

      {openAddBank && businessId && (
        <AddBanks setOpenAddBank={setOpenAddBank} businessID={businessId} />
      )}

      {bankId && (
        <BankDetail
          setOpenBank={(next: boolean) => !next && setBankId(null)}
          bankId={bankId}
          banks={banks}
          businessID={businessId}
        />
      )}
    </div>
  );
};

export default Banks;
